import { useEffect, useMemo, useState } from "react";
import { getLastPrompt, estimateTokenCount, type LastPromptEntry } from "@/lib/api";

type Tool = { type: "function"; function: { name: string; description: string; parameters: unknown } };

function sectionRow(label: string, chars: number) {
  return { label, chars, tokens: Math.round(chars / 4) };
}

export function PromptInspector({ chatId, onClose }: { chatId: string | null; onClose: () => void }) {
  const [entry, setEntry] = useState<LastPromptEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [raw, setRaw] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!chatId) { setEntry(null); setLoading(false); return; }
    setLoading(true);
    getLastPrompt(chatId).then((e) => { if (!cancelled) { setEntry(e); setLoading(false); } });
    return () => { cancelled = true; };
  }, [chatId]);

  const breakdown = useMemo(() => {
    if (!entry) return null;
    const body = entry.body as Record<string, unknown>;
    const messages = (body.messages ?? []) as Array<{ role: string; content: unknown }>;
    const tools = (body.tools ?? []) as Tool[];
    const sections: { label: string; chars: number; tokens: number }[] = [];

    let systemChars = 0;
    let userChars = 0;
    let assistantChars = 0;
    let toolMsgChars = 0;
    for (const m of messages) {
      const c = typeof m.content === "string"
        ? m.content
        : Array.isArray(m.content)
          ? (m.content as Array<{ type: string; text?: string }>).map((p) => p.text ?? "").join("")
          : JSON.stringify(m.content ?? "");
      if (m.role === "system") systemChars += c.length;
      else if (m.role === "user") userChars += c.length;
      else if (m.role === "assistant") assistantChars += c.length;
      else if (m.role === "tool") toolMsgChars += c.length;
    }
    if (systemChars > 0) sections.push(sectionRow("system message", systemChars));
    if (userChars > 0) sections.push(sectionRow("user messages", userChars));
    if (assistantChars > 0) sections.push(sectionRow("assistant messages", assistantChars));
    if (toolMsgChars > 0) sections.push(sectionRow("tool responses", toolMsgChars));

    const toolBreakdown = tools.map((t) => {
      const name = t.function.name;
      const desc = t.function.description ?? "";
      const params = JSON.stringify(t.function.parameters ?? {});
      const chars = name.length + desc.length + params.length;
      return { name, descChars: desc.length, paramChars: params.length, chars, tokens: estimateTokenCount(name + desc + params) };
    });
    const totalToolChars = toolBreakdown.reduce((s, t) => s + t.chars, 0);
    if (totalToolChars > 0) sections.push(sectionRow(`tools (${tools.length})`, totalToolChars));

    const grandChars = sections.reduce((s, x) => s + x.chars, 0);
    const grandTokens = sections.reduce((s, x) => s + x.tokens, 0);
    return { sections, toolBreakdown, grandChars, grandTokens };
  }, [entry]);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b px-4 py-2.5">
          <i className="fa-solid fa-file-magnifying-glass text-primary" />
          <h2 className="text-sm font-semibold">Last prompt sent to LLM</h2>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setRaw((v) => !v)}
              className="rounded-md border px-2.5 py-1 text-xs hover:bg-muted"
            >
              {raw ? "Show breakdown" : "Show raw JSON"}
            </button>
            <button onClick={onClose} className="rounded-md px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground">
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : !entry ? (
            <p className="text-sm text-muted-foreground">
              No prompt captured yet for this chat. Send a message first.
            </p>
          ) : raw ? (
            <pre className="whitespace-pre-wrap break-all font-mono text-[11px] leading-snug">
              {JSON.stringify(entry.body, null, 2)}
            </pre>
          ) : breakdown ? (
            <div className="space-y-4 text-sm">
              <div className="flex items-baseline gap-3 text-xs text-muted-foreground">
                <span>Round {entry.round}</span>
                <span>·</span>
                <span>{new Date(entry.sentAt).toLocaleTimeString()}</span>
                <span className="ml-auto">
                  total: {breakdown.grandChars.toLocaleString()} ch / ~{breakdown.grandTokens.toLocaleString()} tok
                </span>
              </div>

              <div>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sections</h3>
                <table className="w-full text-xs">
                  <tbody>
                    {breakdown.sections.map((s) => (
                      <tr key={s.label} className="border-b last:border-0">
                        <td className="py-1.5">{s.label}</td>
                        <td className="py-1.5 text-right font-mono">{s.chars.toLocaleString()} ch</td>
                        <td className="py-1.5 text-right font-mono w-24">~{s.tokens.toLocaleString()} tok</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {breakdown.toolBreakdown.length > 0 && (
                <div>
                  <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Per-tool ({breakdown.toolBreakdown.length})
                  </h3>
                  <table className="w-full text-xs">
                    <tbody>
                      {breakdown.toolBreakdown
                        .slice()
                        .sort((a, b) => b.chars - a.chars)
                        .map((t) => (
                          <tr key={t.name} className="border-b last:border-0">
                            <td className="py-1.5 font-mono">{t.name}</td>
                            <td className="py-1.5 text-right font-mono text-muted-foreground">
                              desc {t.descChars} · schema {t.paramChars}
                            </td>
                            <td className="py-1.5 text-right font-mono w-24">~{t.tokens} tok</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">System message</h3>
                <pre className="whitespace-pre-wrap rounded-md border bg-muted/30 p-3 font-mono text-[11px] leading-snug">
                  {(() => {
                    const messages = (entry.body.messages ?? []) as Array<{ role: string; content: unknown }>;
                    const sys = messages.find((m) => m.role === "system");
                    if (!sys) return "(none)";
                    return typeof sys.content === "string" ? sys.content : JSON.stringify(sys.content, null, 2);
                  })()}
                </pre>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
